<template>
  <div
    v-for="def in CHANNELS"
    :key="def.label"
    class="flex min-w-0 grow flex-col items-center gap-1"
  >
    <input
      v-model="draft[def.id]"
      class="w-full rounded-md border border-border bg-transparent px-2.5 py-1.5 text-center text-sm focus:outline focus:outline-2 focus:-outline-offset-1 focus:outline-blue-500"
      inputmode="numeric"
      pattern="[0-9]*"
      @blur="reset()"
    />
    <span class="sr-only">{{ def.label }}</span>
  </div>
</template>

<script setup lang="ts">
import type { Hsl } from "culori/fn";
import isDeepEqual from "fast-deep-equal";
import { ref, watch } from "vue";

const CHANNELS = [
  { id: "h", label: "H" },
  { id: "s", label: "S" },
  { id: "l", label: "L" },
] as const;

const modelValue = defineModel<Hsl>({ required: true });

let snapshot: Hsl | undefined;
const draft = ref({ h: "0", s: "0", l: "0" });

const reset = () => {
  if (!snapshot) {
    return;
  }

  draft.value = {
    h: "" + Math.round(snapshot.h ?? 0),
    s: "" + Math.round(snapshot.s * 100),
    l: "" + Math.round(snapshot.l * 100),
  };
};

function quantize(hsl: Hsl): Hsl {
  return {
    mode: "hsl",
    h: Math.round(hsl.h ?? 0),
    s: Math.round(hsl.s * 100) / 100,
    l: Math.round(hsl.l * 100) / 100,
  };
}

watch(
  modelValue,
  (hsl) => {
    const rounded = quantize(hsl);
    if (isDeepEqual(rounded, snapshot)) {
      return;
    }

    snapshot = rounded;
    reset();
  },
  { immediate: true }
);

const parseChannel = (raw: string, min: number, max: number): number => {
  let num = Number(raw);
  if (!Number.isFinite(num)) {
    num = 0;
  }

  return Math.max(min, Math.min(num, max));
};

watch(
  draft,
  (draft) => {
    const h = parseChannel(draft.h, 0, 360);
    const s = parseChannel(draft.s, 0, 100) / 100;
    const l = parseChannel(draft.l, 0, 100) / 100;

    const next: Hsl = { mode: "hsl", h, s, l };
    if (isDeepEqual(next, snapshot)) {
      return;
    }

    snapshot = next;
    modelValue.value = next;
  },
  { deep: true }
);
</script>
